import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import Modal from "../../components/Modal";
import ProductOptionAssignments from "./ProductOptionAssignments";

import {
  deleteProduct,
  listCategories,
  listProducts,
  listProductSizes,
  listSizeTemplates,
  setProductAvailability,
  syncProductSizesForTemplates,
  updateProductDisplayOrders,
  updateProductSizes,
  upsertProduct,
  uploadProductImage,
  type ProductRecord,
  type ProductSizeRecord,
  type SizeSelectionInput,
  type UpsertProductInput,
} from "../../services/catalog";

const productSchema = z.object({
  name: z.string().min(2, "Ürün adı zorunludur"),
  description: z.string().optional(),
  category: z.string().min(1, "Bir kategori seçin"),
  imageUrl: z
    .string()
    .url("Geçerli bir URL girin")
    .optional()
    .or(z.literal("")),
  ingredients: z.string().optional(),
  isAvailable: z.boolean().default(true),
});

type ProductFormValues = z.infer<typeof productSchema>;

const DEFAULT_VALUES: ProductFormValues = {
  name: "",
  description: "",
  category: "",
  imageUrl: "",
  ingredients: "",
  isAvailable: true,
};

type PriceMode = "local" | "delivery";
type SizeSelectionState = Record<
  string,
  {
    enabled: boolean;
    localPrice: number;
    deliveryPrice: number | null;
  }
>;
type PriceDraftsBySizeId = Record<
  string,
  Partial<Record<PriceMode, number>>
>;

const PRICE_MODES: Array<{ value: PriceMode; label: string }> = [
  { value: "local", label: "Gel-al / Restoranda ye" },
  { value: "delivery", label: "Teslimat" },
];
const SIZE_PRIORITY_SLUGS = ["size_standard", "size_small", "size_medium", "size_large"];
const SIZE_PRICE_COLUMNS = [
  { slug: "size_small", label: "Küçük" },
  { slug: "size_medium", label: "Orta" },
  { slug: "size_large", label: "Büyük" },
  { slug: "size_standard", label: "Standart" },
];

const CATEGORY_LABELS: Record<string, string> = {
  avantajli: "Avantajlı Menüler",
  favori: "Favori Pizzalar",
  sucuklu: "Sucuklu Pizzalar",
  etli: "Etli Pizzalar",
  tavuklu: "Tavuklu Pizzalar",
  deniz: "Deniz Ürünleri Pizzaları",
  gurme: "Gurme Serisi",
  atistirmalik: "Atıştırmalıklar",
  soslar: "Soslar",
  icecekler: "İçecekler",
};

const CATEGORY_ORDER: string[] = [
  "avantajli",
  "favori",
  "sucuklu",
  "etli",
  "tavuklu",
  "deniz",
  "gurme",
  "atistirmalik",
  "soslar",
  "icecekler",
];

const LEGACY_CATEGORY_MAP: Record<string, string> = {
  classic: "favori",
  specialty: "favori",
  vegetarian: "favori",
  meat: "etli",
  "meat lovers": "etli",
  chicken: "tavuklu",
  seafood: "deniz",
  pizzas: "favori",
  pizza: "favori",
  appetizers: "atistirmalik",
  snacks: "atistirmalik",
  drinks: "icecekler",
  beverages: "icecekler",
  dips: "atistirmalik",
  desserts: "favori",
  "öğrenci menüsü": "avantajli",
  "ogrenci menusu": "avantajli",
  avantajli: "avantajli",
  "avantajli menüler": "avantajli",
  "avantajlı menüler": "avantajli",
  "avantajli menuler": "avantajli",
  "avantajlı menuler": "avantajli",
  soslar: "soslar",
};

const normalizeCategory = (value: string | null | undefined): string => {
  if (!value) return "favori";
  const normalized = value.trim().toLowerCase();
  if (LEGACY_CATEGORY_MAP[normalized]) {
    return LEGACY_CATEGORY_MAP[normalized];
  }
  return normalized;
};

const getCategoryLabel = (slug: string) => {
  if (CATEGORY_LABELS[slug]) return CATEGORY_LABELS[slug];
  return slug
    .split(/[-_\s]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const areSizeSelectionsEqual = (a: SizeSelectionState, b: SizeSelectionState) => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    const aValue = a[key];
    const bValue = b[key];
    if (!bValue) return false;
    if (
      aValue.enabled !== bValue.enabled ||
      aValue.localPrice !== bValue.localPrice ||
      aValue.deliveryPrice !== bValue.deliveryPrice
    ) {
      return false;
    }
  }
  return true;
};

function stringifyIngredients(list: string[] | undefined) {
  if (!Array.isArray(list) || list.length === 0) return "";
  return list.join(", ");
}

function parseIngredients(raw: string | undefined) {
  if (!raw) return [] as string[];
  return raw
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

const getLocalPrice = (size: ProductSizeRecord): number =>
  size.priceOverride ?? size.defaultPrice ?? 0;

const getPersistedPrice = (
  size: ProductSizeRecord,
  priceMode: PriceMode,
): number =>
  priceMode === "delivery"
    ? size.deliveryPriceOverride ?? getLocalPrice(size)
    : getLocalPrice(size);

const getEffectivePrice = (
  size: ProductSizeRecord,
  priceMode: PriceMode,
  drafts: Partial<Record<PriceMode, number>> | undefined,
): number => {
  const modeDraft = drafts?.[priceMode];
  if (modeDraft !== undefined) {
    return modeDraft;
  }

  return priceMode === "delivery" && size.deliveryPriceOverride == null
    ? drafts?.local ?? getLocalPrice(size)
    : getPersistedPrice(size, priceMode);
};

function PriceModeToggle({
  value,
  onChange,
}: {
  value: PriceMode;
  onChange: (priceMode: PriceMode) => void;
}) {
  return (
    <div className="segmented-control" role="group" aria-label="Fiyat sipariş tipi">
      {PRICE_MODES.map((mode) => (
        <button
          key={mode.value}
          type="button"
          className={value === mode.value ? "active" : ""}
          aria-pressed={value === mode.value}
          onClick={() => onChange(mode.value)}
        >
          {mode.label}
        </button>
      ))}
    </div>
  );
}

function InlinePriceInput({
  size,
  priceMode,
  drafts,
  onDraftChange,
}: {
  size: ProductSizeRecord | null;
  priceMode: PriceMode;
  drafts?: Partial<Record<PriceMode, number>>;
  onDraftChange: (
    sizeId: string,
    priceMode: PriceMode,
    price: number | null,
  ) => void;
}) {
  const [value, setValue] = useState<string>("");
  const baselinePrice = size
    ? getEffectivePrice(size, priceMode, {
        ...drafts,
        [priceMode]: undefined,
      })
    : 0;
  const effectivePrice = size ? getEffectivePrice(size, priceMode, drafts) : null;

  useEffect(() => {
    if (!size) {
      setValue("");
      return;
    }
    setValue((effectivePrice ?? 0).toString());
  }, [effectivePrice, size]);

  const commit = () => {
    if (!size) return;
    const normalizedValue = value.trim() === "" ? "0" : value;
    const parsed = Number(normalizedValue);
    if (!Number.isFinite(parsed) || parsed < 0) {
      const fallback = effectivePrice ?? 0;
      setValue(fallback.toString());
      return;
    }
    setValue(parsed.toString());
    if (parsed === baselinePrice) {
      onDraftChange(size.id, priceMode, null);
      return;
    }
    onDraftChange(size.id, priceMode, parsed);
  };

  return (
    <input
      type="number"
      step={0.5}
      min={0}
      className="inline-input size-price-input compact"
      disabled={!size}
      placeholder={size ? "0" : "—"}
      value={value}
      onClick={(event) => event.stopPropagation()}
      onFocus={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onChange={(event) => setValue(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.currentTarget.blur();
        }
      }}
    />
  );
}

interface ProductTableProps {
  sections: {
    id: string;
    label: string;
    products: ProductRecord[];
  }[];
  sizeColumns: { slug: string; label: string }[];
  sizeByProductId: Record<string, Record<string, ProductSizeRecord>>;
  priceMode: PriceMode;
  priceDraftsBySizeId: PriceDraftsBySizeId;
  expandedProductId: string | null;
  renderExpanded: (product: ProductRecord) => React.ReactNode;
  onRowClick: (product: ProductRecord) => void;
  onPriceDraftChange: (
    sizeId: string,
    priceMode: PriceMode,
    price: number | null,
  ) => void;
  onDelete: (product: ProductRecord) => void;
  onToggleAvailability: (product: ProductRecord) => void;
  onReorderProduct: (
    sectionId: string,
    draggedProductId: string,
    targetProductId: string,
    placement: "before" | "after",
  ) => void;
}

function ProductTable({
  sections,
  sizeColumns,
  sizeByProductId,
  priceMode,
  priceDraftsBySizeId,
  expandedProductId,
  renderExpanded,
  onRowClick,
  onPriceDraftChange,
  onDelete,
  onToggleAvailability,
  onReorderProduct,
}: ProductTableProps) {
  const [draggedProduct, setDraggedProduct] = useState<{
    sectionId: string;
    productId: string;
  } | null>(null);
  const totalProducts = sections.reduce(
    (total, section) => total + section.products.length,
    0,
  );
  const columnCount = 3 + sizeColumns.length;

  if (totalProducts === 0) {
    return (
      <div className="empty-state">
        <strong>Henüz ürün yok</strong>
        <p>Sağdaki formu kullanarak ilk ürününüzü ekleyin.</p>
      </div>
    );
  }

  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>Ad</th>
          {sizeColumns.map((column) => (
            <th key={column.slug}>{column.label}</th>
          ))}
          <th>Durum</th>
          <th>İşlemler</th>
        </tr>
      </thead>
      <tbody>
        {sections.map((section) => (
          <Fragment key={section.id}>
            <tr className="table-section">
              <td colSpan={columnCount}>{section.label}</td>
            </tr>
            {section.products.map((product) => (
              <Fragment key={product.id}>
                <tr
                  className={[
                    "product-sort-row",
                    expandedProductId === product.id ? "active" : "",
                    draggedProduct?.productId === product.id ? "is-dragging" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onDragOver={(event) => {
                    if (draggedProduct && draggedProduct.sectionId === section.id) {
                      event.preventDefault();
                      event.dataTransfer.dropEffect = "move";
                    }
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    const draggedProductId =
                      draggedProduct?.productId || event.dataTransfer.getData("text/plain");
                    setDraggedProduct(null);
                    if (!draggedProductId || draggedProductId === product.id) {
                      return;
                    }
                    const rowBounds = event.currentTarget.getBoundingClientRect();
                    const placement =
                      event.clientY > rowBounds.top + rowBounds.height / 2
                        ? "after"
                        : "before";
                    onReorderProduct(section.id, draggedProductId, product.id, placement);
                  }}
                  onDragEnd={() => setDraggedProduct(null)}
                  onClick={() => onRowClick(product)}
                >
                  <td>
                    <div className="product-sort-cell">
                      <button
                        type="button"
                        className="drag-handle"
                        aria-label={`${product.name} sıralamasını değiştir`}
                        draggable
                        onClick={(event) => event.stopPropagation()}
                        onDragStart={(event) => {
                          event.stopPropagation();
                          setDraggedProduct({ sectionId: section.id, productId: product.id });
                          event.dataTransfer.effectAllowed = "move";
                          event.dataTransfer.setData("text/plain", product.id);
                        }}
                        onDragEnd={() => setDraggedProduct(null)}
                      >
                        ::
                      </button>
                      <div>
                        <strong>{product.name}</strong>
                        {product.description ? (
                          <p className="helper-text">{product.description}</p>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  {sizeColumns.map((column) => {
                    const size = sizeByProductId[product.id]?.[column.slug] ?? null;
                    return (
                      <td key={`${product.id}-${column.slug}`}>
                        <InlinePriceInput
                          size={size}
                          priceMode={priceMode}
                          drafts={size ? priceDraftsBySizeId[size.id] : undefined}
                          onDraftChange={onPriceDraftChange}
                        />
                      </td>
                    );
                  })}
                  <td>
                    <label
                      className="toggle-switch"
                      aria-label="Ürün durumu"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={product.isAvailable}
                        onChange={() => onToggleAvailability(product)}
                      />
                      <span className="toggle-slider" />
                    </label>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="button danger"
                      onClick={(event) => {
                        event.stopPropagation();
                        onDelete(product);
                      }}
                    >
                      Sil
                    </button>
                  </td>
                </tr>
                {expandedProductId === product.id ? (
                  <tr className="table-expansion">
                    <td colSpan={columnCount}>{renderExpanded(product)}</td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

export default function ProductManager() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [priceMode, setPriceMode] = useState<PriceMode>("local");
  const [editingProduct, setEditingProduct] = useState<ProductRecord | null>(
    null,
  );
  const [sizeSelections, setSizeSelections] = useState<SizeSelectionState>({});
  const [sizeValidationError, setSizeValidationError] = useState<string | null>(
    null,
  );
  const [priceDraftsBySizeId, setPriceDraftsBySizeId] =
    useState<PriceDraftsBySizeId>({});
  const priceDraftsRef = useRef<PriceDraftsBySizeId>({});

  const { data: categories = [] } = useQuery({
    queryKey: ["catalog", "categories"],
    queryFn: listCategories,
  });

  const {
    data: products = [],
    isLoading: productsLoading,
    isFetching: productsFetching,
  } = useQuery({
    queryKey: ["catalog", "products"],
    queryFn: listProducts,
  });

  const { data: sizeTemplates = [] } = useQuery({
    queryKey: ["catalog", "size-templates"],
    queryFn: listSizeTemplates,
  });

  const { data: productSizes = [] } = useQuery({
    queryKey: ["catalog", "product_sizes"],
    queryFn: listProductSizes,
  });

  const productSizeById = useMemo(() => {
    const map: Record<string, ProductSizeRecord> = {};
    productSizes.forEach((size) => {
      map[size.id] = size;
    });
    return map;
  }, [productSizes]);

  useEffect(() => {
    priceDraftsRef.current = priceDraftsBySizeId;
  }, [priceDraftsBySizeId]);

  useEffect(() => {
    setPriceDraftsBySizeId((previousDrafts) => {
      let hasChanges = false;
      const nextDrafts: PriceDraftsBySizeId = {};

      Object.entries(previousDrafts).forEach(([sizeId, sizeDrafts]) => {
        const size = productSizeById[sizeId];
        if (!size) {
          hasChanges = true;
          return;
        }

        const nextSizeDrafts: Partial<Record<PriceMode, number>> = {};
        PRICE_MODES.forEach(({ value: mode }) => {
          const draftPrice = sizeDrafts[mode];
          if (draftPrice === undefined) {
            return;
          }

          if (getPersistedPrice(size, mode) === draftPrice) {
            hasChanges = true;
            return;
          }

          nextSizeDrafts[mode] = draftPrice;
        });

        if (Object.keys(nextSizeDrafts).length > 0) {
          nextDrafts[sizeId] = nextSizeDrafts;
        }
      });

      return hasChanges ? nextDrafts : previousDrafts;
    });
  }, [productSizeById]);

  const categorizedProducts = useMemo(() => {
    const categoryMap = new Map<string, ProductRecord[]>();

    products.forEach((product) => {
      const normalized = normalizeCategory(product.category);
      if (!categoryMap.has(normalized)) {
        categoryMap.set(normalized, []);
      }
      categoryMap.get(normalized)!.push(product);
    });

    categoryMap.forEach((list) => {
      list.sort((a, b) => {
        const orderDiff = a.displayOrder - b.displayOrder;
        if (orderDiff !== 0) {
          return orderDiff;
        }
        return a.name.localeCompare(b.name, "tr", { sensitivity: "base" });
      });
    });

    const sections: { id: string; label: string; products: ProductRecord[] }[] = [];

    CATEGORY_ORDER.forEach((slug) => {
      const items = categoryMap.get(slug);
      if (items && items.length > 0) {
        sections.push({
          id: slug,
          label: CATEGORY_LABELS[slug] ?? getCategoryLabel(slug),
          products: items,
        });
        categoryMap.delete(slug);
      }
    });

    const remaining = Array.from(categoryMap.entries()).sort((a, b) =>
      a[0].localeCompare(b[0], "tr", { sensitivity: "base" }),
    );

    remaining.forEach(([slug, items]) => {
      sections.push({
        id: slug,
        label: CATEGORY_LABELS[slug] ?? getCategoryLabel(slug),
        products: items,
      });
    });

    return sections;
  }, [products]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ProductFormValues>({
    resolver: zodResolver(productSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const saveProductMutation = useMutation({
    mutationFn: async ({
      productValues,
      sizeSelections,
    }: {
      productValues: UpsertProductInput;
      sizeSelections: SizeSelectionInput[];
    }) => {
      const product = await upsertProduct(productValues);
      await syncProductSizesForTemplates(product.id, sizeSelections);
      return product;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["catalog", "products"] });
      void queryClient.invalidateQueries({ queryKey: ["catalog", "product_sizes"] });
      setModalOpen(false);
      setEditingProduct(null);
      reset(DEFAULT_VALUES);
      setSizeSelections({});
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (product: ProductRecord) => deleteProduct(product.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["catalog", "products"] });
    },
  });

  const toggleAvailabilityMutation = useMutation({
    mutationFn: async (product: ProductRecord) =>
      setProductAvailability(product.id, !product.isAvailable),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["catalog", "products"] });
    },
  });

  const reorderProductsMutation = useMutation({
    mutationFn: async ({ productIds }: { sectionId: string; productIds: string[] }) => {
      await updateProductDisplayOrders(
        productIds.map((id, index) => ({
          id,
          displayOrder: (index + 1) * 10,
        })),
      );
    },
    onMutate: async ({ productIds }) => {
      await queryClient.cancelQueries({ queryKey: ["catalog", "products"] });
      const previousProducts = queryClient.getQueryData<ProductRecord[]>([
        "catalog",
        "products",
      ]);

      queryClient.setQueryData<ProductRecord[]>(
        ["catalog", "products"],
        (currentProducts) =>
          currentProducts?.map((product) => {
            const nextIndex = productIds.indexOf(product.id);
            if (nextIndex === -1) {
              return product;
            }
            return {
              ...product,
              displayOrder: (nextIndex + 1) * 10,
            };
          }),
      );

      return { previousProducts };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousProducts) {
        queryClient.setQueryData(["catalog", "products"], context.previousProducts);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["catalog", "products"] });
    },
  });

  const savePriceDraftsMutation = useMutation({
    mutationFn: async (drafts: PriceDraftsBySizeId) => {
      const updates: Parameters<typeof updateProductSizes>[0] = [];

      Object.entries(drafts).forEach(([sizeId, sizeDrafts]) => {
        const size = productSizeById[sizeId];
        if (!size) return;

        const localPrice = sizeDrafts.local;
        const deliveryPrice = sizeDrafts.delivery;
        if (localPrice === undefined && deliveryPrice === undefined) {
          return;
        }

        updates.push({
          id: sizeId,
          priceOverride: localPrice,
          deliveryPriceOverride: deliveryPrice,
          isAvailable: size.isAvailable,
        });
      });

      if (updates.length === 0) {
        return;
      }

      await updateProductSizes(updates);
    },
    onSuccess: () => {
      setPriceDraftsBySizeId({});
      void queryClient.invalidateQueries({ queryKey: ["catalog", "product_sizes"] });
      void queryClient.invalidateQueries({ queryKey: ["catalog", "products"] });
    },
  });

  const onSubmit = async (values: ProductFormValues) => {
    const selections = selectedSizePayload();
    if (selections.length === 0) {
      setSizeValidationError("En az bir boyut seçmelisiniz.");
      return;
    }
    setSizeValidationError(null);
    const payload = {
      id: editingProduct?.id,
      name: values.name,
      description: values.description?.trim() || null,
      category: values.category,
      imageUrl: values.imageUrl ? values.imageUrl.trim() : null,
      ingredients: parseIngredients(values.ingredients),
      isAvailable: values.isAvailable,
    };
    await saveProductMutation.mutateAsync({
      productValues: payload,
      sizeSelections: selections,
    });
  };

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingProduct(null);
    reset(DEFAULT_VALUES);
    setSizeSelections({});
  }, [reset]);

  const openCreate = () => {
    setEditingProduct(null);
    setPriceMode("local");
    reset(DEFAULT_VALUES);
    setSizeSelections({});
    setModalOpen(true);
  };

  const handleEdit = (product: ProductRecord) => {
    setEditingProduct(product);
    reset({
      name: product.name,
      description: product.description ?? "",
      category: product.category,
      imageUrl: product.imageUrl ?? "",
      ingredients: stringifyIngredients(product.ingredients),
      isAvailable: product.isAvailable,
    });
  };

  const handleClearSelection = () => {
    setEditingProduct(null);
    reset(DEFAULT_VALUES);
  };

  const handleRowClick = (product: ProductRecord) => {
    if (editingProduct?.id === product.id) {
      handleClearSelection();
      return;
    }
    handleEdit(product);
  };

  const handleReorderProduct = useCallback(
    (
      sectionId: string,
      draggedProductId: string,
      targetProductId: string,
      placement: "before" | "after",
    ) => {
      if (reorderProductsMutation.isPending) {
        return;
      }

      const section = categorizedProducts.find((entry) => entry.id === sectionId);
      if (!section) {
        return;
      }

      const draggedIndex = section.products.findIndex(
        (product) => product.id === draggedProductId,
      );
      const targetIndex = section.products.findIndex(
        (product) => product.id === targetProductId,
      );

      if (draggedIndex === -1 || targetIndex === -1 || draggedIndex === targetIndex) {
        return;
      }

      const nextProducts = section.products.slice();
      const [draggedProduct] = nextProducts.splice(draggedIndex, 1);
      let insertIndex = placement === "after" ? targetIndex + 1 : targetIndex;
      if (draggedIndex < insertIndex) {
        insertIndex -= 1;
      }
      nextProducts.splice(insertIndex, 0, draggedProduct);

      reorderProductsMutation.mutate({
        sectionId,
        productIds: nextProducts.map((product) => product.id),
      });
    },
    [categorizedProducts, reorderProductsMutation],
  );

  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const imagePreview = watch("imageUrl");

  const handleSelectImage = () => {
    fileInputRef.current?.click();
  };

  const handleImageFileChange: React.ChangeEventHandler<
    HTMLInputElement
  > = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    setImageUploadError(null);
    try {
      const url = await uploadProductImage(file);
      setValue("imageUrl", url, { shouldDirty: true });
    } catch (error) {
      console.error("Image upload failed", error);
      setImageUploadError(
        error instanceof Error
          ? error.message
          : "Dosya yüklenemedi. Lütfen tekrar deneyin.",
      );
    } finally {
      setUploadingImage(false);
      event.target.value = "";
    }
  };

  const categorySuggestions = useMemo(() => {
    return categories
      ?.slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((category) => ({
        slug: category.slug,
        name: category.name,
      })) ?? [];
  }, [categories]);

  const sizeByProductId = useMemo(() => {
    const map: Record<string, Record<string, ProductSizeRecord>> = {};
    productSizes.forEach((size) => {
      const slug = size.sizeTemplateSlug ?? "";
      if (!slug) return;
      if (!map[size.productId]) {
        map[size.productId] = {};
      }
      map[size.productId]![slug] = size;
    });
    return map;
  }, [productSizes]);

  const editingProductSizes = useMemo(() => {
    if (!editingProduct) return [];
    return productSizes.filter((size) => size.productId === editingProduct.id);
  }, [editingProduct, productSizes]);

  const pendingPriceChangeCount = Object.values(priceDraftsBySizeId).reduce(
    (count, drafts) =>
      count +
      (drafts.local !== undefined ? 1 : 0) +
      (drafts.delivery !== undefined ? 1 : 0),
    0,
  );

  const setPriceDraft = (
    sizeId: string,
    mode: PriceMode,
    nextPrice: number | null,
  ) => {
    const size = productSizeById[sizeId];
    if (!size) return;

    const baselinePrice = getEffectivePrice(size, mode, {
      ...priceDraftsBySizeId[sizeId],
      [mode]: undefined,
    });
    const normalizedPrice =
      nextPrice == null || nextPrice === baselinePrice ? null : nextPrice;

    setPriceDraftsBySizeId((previousDrafts) => {
      const previousSizeDrafts = previousDrafts[sizeId] ?? {};
      if (normalizedPrice == null) {
        if (previousSizeDrafts[mode] === undefined) return previousDrafts;
        const nextSizeDrafts = { ...previousSizeDrafts };
        delete nextSizeDrafts[mode];
        const nextDrafts = { ...previousDrafts };
        if (Object.keys(nextSizeDrafts).length === 0) {
          delete nextDrafts[sizeId];
        } else {
          nextDrafts[sizeId] = nextSizeDrafts;
        }
        return nextDrafts;
      }

      if (previousSizeDrafts[mode] === normalizedPrice) {
        return previousDrafts;
      }

      return {
        ...previousDrafts,
        [sizeId]: {
          ...previousSizeDrafts,
          [mode]: normalizedPrice,
        },
      };
    });

    if (editingProduct?.id === size.productId && size.sizeTemplateId) {
      const selectionPrice =
        mode === "local"
          ? normalizedPrice ?? getLocalPrice(size)
          : normalizedPrice ?? size.deliveryPriceOverride ?? null;
      setSizeSelections((previousSelections) => {
        const existingSelection = previousSelections[size.sizeTemplateId!];
        if (!existingSelection) return previousSelections;
        if (
          mode === "local" &&
          existingSelection.localPrice === selectionPrice
        ) {
          return previousSelections;
        }
        if (
          mode === "delivery" &&
          existingSelection.deliveryPrice === selectionPrice
        ) {
          return previousSelections;
        }
        return {
          ...previousSelections,
          [size.sizeTemplateId!]: {
            ...existingSelection,
            ...(mode === "local"
              ? { localPrice: selectionPrice as number }
              : { deliveryPrice: selectionPrice }),
          },
        };
      });
    }
  };

  useEffect(() => {
    if (!editingProduct) return;
    const next: SizeSelectionState = {};
    editingProductSizes.forEach((size) => {
      if (!size.sizeTemplateId) return;
      const drafts = priceDraftsRef.current[size.id];
      next[size.sizeTemplateId] = {
        enabled: true,
        localPrice: drafts?.local ?? getLocalPrice(size),
        deliveryPrice:
          drafts?.delivery ?? size.deliveryPriceOverride ?? null,
      };
    });
    setSizeSelections((prev) => (areSizeSelectionsEqual(prev, next) ? prev : next));
  }, [editingProduct, editingProductSizes]);

  useEffect(() => {
    if (editingProduct) return;
    setSizeSelections((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const standardTemplate = sizeTemplates.find(
        (template) => template.slug === "size_standard",
      );
      if (!standardTemplate) return prev;
      return {
        [standardTemplate.id]: {
          enabled: true,
          localPrice: 0,
          deliveryPrice: null,
        },
      };
    });
  }, [editingProduct, sizeTemplates]);

  const toggleSizeTemplate = (templateId: string, enabled: boolean) => {
    setSizeSelections((prev) => ({
      ...prev,
      [templateId]: {
        enabled,
        localPrice: prev[templateId]?.localPrice ?? 0,
        deliveryPrice: prev[templateId]?.deliveryPrice ?? null,
      },
    }));
  };

  const handleSizePriceChange = (templateId: string, price: number) => {
    const normalized = Number.isFinite(price) ? Math.max(0, price) : 0;
    setSizeSelections((prev) => {
      const current = prev[templateId];
      const nextSelection = {
        enabled: current?.enabled ?? true,
        localPrice: current?.localPrice ?? 0,
        deliveryPrice: current?.deliveryPrice ?? null,
      };

      if (priceMode === "local") {
        nextSelection.localPrice = normalized;
      } else {
        nextSelection.deliveryPrice = normalized;
      }

      return {
        ...prev,
        [templateId]: nextSelection,
      };
    });

    const matchingSize = editingProductSizes.find(
      (size) => size.sizeTemplateId === templateId,
    );
    if (matchingSize) {
      setPriceDraft(matchingSize.id, priceMode, normalized);
    }
  };

  const selectedSizePayload = (): SizeSelectionInput[] => {
    const entries = Object.entries(sizeSelections).filter(([, data]) => data.enabled);
    if (entries.length === 0) {
      return [];
    }
    const templateMap: Record<string, typeof sizeTemplates[0]> = {};
    sizeTemplates.forEach((template) => {
      templateMap[template.id] = template;
    });
    const priorityIndex = (templateId: string) => {
      const slug = templateMap[templateId]?.slug ?? "";
      const idx = SIZE_PRIORITY_SLUGS.indexOf(slug);
      return idx === -1 ? SIZE_PRIORITY_SLUGS.length : idx;
    };
    const defaultEntry = [...entries].sort(
      (a, b) => priorityIndex(a[0]) - priorityIndex(b[0]),
    )[0];
    const defaultId = defaultEntry?.[0] ?? entries[0][0];
    return entries.map(([templateId, data]) => ({
      sizeTemplateId: templateId,
      priceOverride: data.localPrice,
      deliveryPriceOverride: data.deliveryPrice ?? data.localPrice,
      isDefault: templateId === defaultId,
    }));
  };

  const renderProductForm = () => (
    <form className="form-grid" onSubmit={handleSubmit(onSubmit)}>
      <div className="form-row">
        <label className="form-field">
          <span>Ürün adı</span>
          <input
            type="text"
            placeholder="Trüf Mantarlı"
            {...register("name")}
          />
          {errors.name ? (
            <span className="error-text">{errors.name.message}</span>
          ) : null}
        </label>
        <label className="form-field">
          <span>Kategori slug</span>
          <input
            type="text"
            list="category-slug-options"
            placeholder="örn. avantajli"
            {...register("category")}
          />
          <datalist id="category-slug-options">
            {categorySuggestions.map((category) => (
              <option key={category.slug} value={category.slug}>
                {category.name} ({category.slug})
              </option>
            ))}
          </datalist>
          <span className="helper-text">
            Slug değerini seçin veya yazın. Örn: <code>pizza</code>,{" "}
            <code>advantage</code>.
          </span>
          {errors.category ? (
            <span className="error-text">{errors.category.message}</span>
          ) : null}
        </label>
      </div>

      <div className="form-grid two-columns">
        <label className="form-field">
          <span>Açıklama</span>
          <textarea
            rows={3}
            className="compact-textarea"
            placeholder="Malzemeleri ve pişirme stilini belirtin..."
            {...register("description")}
          />
        </label>
        <label className="form-field">
          <span>İçindekiler (virgülle ayırın)</span>
          <input
            type="text"
            placeholder="Mozzarella, trüf yağı, mantar"
            {...register("ingredients")}
          />
        </label>
      </div>

      <div className="form-grid two-columns">
        <div className="form-field">
          <span>Boyut seçenekleri (isme göre düzenlenir)</span>
          <p className="helper-text">
            Varsayılan fiyat sırası: Standart → Küçük → Orta → Büyük. Seçili
            en yüksek öncelikli boyut otomatik olarak baz fiyat olur.
          </p>
          <PriceModeToggle value={priceMode} onChange={setPriceMode} />
          <p className="helper-text">
            {priceMode === "local"
              ? "Gel-al ve restoranda ye siparişleri aynı fiyatı kullanır."
              : "Teslimat fiyatı yerel fiyattan bağımsız olarak belirlenir."}
          </p>
          {sizeValidationError ? (
            <p className="input-error">{sizeValidationError}</p>
          ) : null}
          <div className="form-grid">
            {[...sizeTemplates]
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map((template) => {
                const selection = sizeSelections[template.id] ?? {
                  enabled: false,
                  localPrice: 0,
                  deliveryPrice: null,
                };
                const selectedPrice =
                  priceMode === "delivery"
                    ? selection.deliveryPrice ?? selection.localPrice
                    : selection.localPrice;
                return (
                  <div
                    key={template.id}
                    className="form-row center"
                  >
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={selection.enabled}
                        onChange={(event) =>
                          toggleSizeTemplate(template.id, event.target.checked)
                        }
                      />
                      <span>{template.name}</span>
                    </label>
                    <input
                      type="number"
                      className="inline-input size-price-input"
                      step="0.5"
                      min="0"
                      disabled={!selection.enabled}
                      value={selectedPrice}
                      aria-label={`${template.name} ${
                        priceMode === "delivery"
                          ? "teslimat"
                          : "gel-al ve restoranda"
                      } fiyatı`}
                      onChange={(event) =>
                        handleSizePriceChange(
                          template.id,
                          Number(event.target.value),
                        )
                      }
                    />
                  </div>
                );
              })}
          </div>
        </div>

        <label className="form-field">
          <span>Görsel bağlantısı</span>
          <div className="input-with-button">
            <input
              type="url"
              placeholder="https://"
              {...register("imageUrl")}
            />
            <button
              type="button"
              className="button secondary"
              onClick={handleSelectImage}
              disabled={uploadingImage}
            >
              {uploadingImage ? "Yükleniyor..." : "Dosya yükle"}
            </button>
          </div>
          <span className="helper-text">
            Supabase Storage bağlantısı ekleyebilir veya yeni görsel
            yükleyebilirsiniz.
          </span>
          {errors.imageUrl ? (
            <span className="error-text">{errors.imageUrl.message}</span>
          ) : null}
          {imageUploadError ? (
            <span className="error-text">{imageUploadError}</span>
          ) : null}
          {imagePreview ? (
            <div className="product-image-preview">
              <img src={imagePreview} alt="Ürün önizlemesi" />
            </div>
          ) : null}
        </label>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleImageFileChange}
      />

      <label className="form-field checkbox">
        <input type="checkbox" {...register("isAvailable")} />
        <span>Ürün müşteri uygulamalarında görünsün</span>
      </label>

      <div className="form-actions">
        {editingProduct ? (
          <button type="button" className="button ghost" onClick={handleClearSelection}>
            İptal
          </button>
        ) : (
          <button type="button" className="button ghost" onClick={closeModal}>
            İptal
          </button>
        )}
        <button
          type="submit"
          className="button primary"
          disabled={isSubmitting || saveProductMutation.isPending}
        >
          {editingProduct ? "Değişiklikleri kaydet" : "Ürün ekle"}
        </button>
      </div>
    </form>
  );

  const expandedContent = editingProduct ? (
    <div className="table-expansion-content">
      <div className="card">
        <div className="card-header">
          <div>
            <p className="card-title">{editingProduct.name} düzenleniyor</p>
            <p className="card-subtitle">
              Fiyat, görsel ve görünürlük ayarlarını buradan düzenleyin.
            </p>
          </div>
        </div>
        {renderProductForm()}
      </div>
      <ProductOptionAssignments
        productId={editingProduct.id}
        sizes={editingProductSizes}
      />
    </div>
  ) : null;

  const handleSavePriceDrafts = () => {
    if (pendingPriceChangeCount === 0) return;
    savePriceDraftsMutation.mutate(priceDraftsBySizeId);
  };

  return (
    <>
      <div className="card">
        <div className="card-header">
          <div>
            <p className="card-title">Menü ürünleri</p>
            <p className="card-subtitle">
              {productsLoading || productsFetching
                ? "Katalog Supabase'den yükleniyor..."
                : `${products.length} ürün listeleniyor · ${
                    priceMode === "delivery"
                      ? "Teslimat fiyatları"
                      : "Gel-al / Restoranda fiyatları"
                  }`}
            </p>
          </div>
          <div className="table-actions">
            <PriceModeToggle value={priceMode} onChange={setPriceMode} />
            {reorderProductsMutation.isPending ? (
              <span className="helper-text">Sıralama kaydediliyor...</span>
            ) : null}
            {pendingPriceChangeCount > 0 ? (
              <>
                <span className="helper-text">
                  {pendingPriceChangeCount} fiyat değişikliği hazır
                </span>
                <button
                  type="button"
                  className="button secondary"
                  onClick={handleSavePriceDrafts}
                  disabled={savePriceDraftsMutation.isPending}
                >
                  {savePriceDraftsMutation.isPending
                    ? "Kaydediliyor..."
                    : "Fiyatları kaydet"}
                </button>
              </>
            ) : null}
            <button type="button" className="button primary" onClick={openCreate}>
              + Ürün Ekle
            </button>
          </div>
        </div>

        <ProductTable
          sections={categorizedProducts}
          sizeColumns={SIZE_PRICE_COLUMNS}
          sizeByProductId={sizeByProductId}
          priceMode={priceMode}
          priceDraftsBySizeId={priceDraftsBySizeId}
          expandedProductId={editingProduct?.id ?? null}
          renderExpanded={() => expandedContent}
          onRowClick={handleRowClick}
          onPriceDraftChange={setPriceDraft}
          onReorderProduct={handleReorderProduct}
          onDelete={(product) => {
            if (
              window.confirm(
                `${product.name} ürününü silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`,
              )
            ) {
              deleteMutation.mutate(product);
            }
          }}
          onToggleAvailability={(product) =>
            toggleAvailabilityMutation.mutate(product)
          }
        />
      </div>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title="Yeni ürün ekle"
        wide
      >
        {renderProductForm()}
      </Modal>
    </>
  );
}
